import { when } from 'mobx';
import { inject, observer } from 'mobx-react';
import React from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';

import { AnalyticsConsumerProps, withAnalytics } from '../../hoc/withAnalytics';
import { getParams, stripParams } from '../../routing';
import Store from '../../store';
import FeatureFlagsStore from '../../store/featureFlagsStore';
import FlagStore, { FlagType, InternalFlag } from '../../store/flagStore';
import ProductStore, { Site } from '../../store/productStore';

import { XFlowFlagMessages } from './ErrorMessages';
import XflowErrorBoundary from './XflowErrorBoundary';
import XFlowLazy, { XFlowIFrameLazy } from './XflowLazy';

interface Props {
  featureFlagsStore: FeatureFlagsStore;
  flagStore: FlagStore;
  productStore: ProductStore;
}

interface State {
  cloudId?: string; // e.g. DUMMY-158c8204-ff3b-47c2-adbb-a0906ccc722b
  edition?: string; // e.g. premium
  instanceName?: string; // e.g. product-fabric.atlassian.net
  isDismissed?: boolean;
  isEnrolledInExpandDiscovery: boolean;
  migrationSourceUuid?: string; // e.g. 158c8204-ff3b-47c2-adbb-a0906ccc722b
  targetProduct?: string; // e.g. opsgenie
}

export function stripXflowParams() {
  stripParams('startTrialCloudId', 'startTrialInstance', 'startTrialProduct', 'edition', 'migrationSourceUuid');
}

/**
 * Manages the lifecycle of the xflow component, including stripping query parameters when the flow is completed.
 */
@observer
class XflowInit extends React.Component<Props & AnalyticsConsumerProps & InjectedIntlProps, State> {
  state = {
    cloudId: undefined,
    edition: undefined,
    instanceName: undefined,
    isDismissed: false,
    isEnrolledInExpandDiscovery: false,
    migrationSourceUuid: undefined,
    targetProduct: undefined,
  };

  componentDidMount(): void {
    const { startTrialCloudId, startTrialInstance, startTrialProduct, edition, migrationSourceUuid } = getParams(
      'startTrialCloudId',
      'startTrialInstance',
      'startTrialProduct',
      'edition',
      'migrationSourceUuid',
    );

    when(
      () => this.props.featureFlagsStore.hasLoaded && this.props.productStore.hasLoaded,
      () => {
        const { flags } = this.props.featureFlagsStore;
        if (flags['uchi.jsw-jsd-consistent-expand-uchi-ui']) {
          const { availableSites } = this.props.productStore;
          const isEnrolledInExpandDiscovery = this.tenantHasJSWLicence(availableSites, startTrialCloudId);
          this.setState({
            cloudId: startTrialCloudId,
            edition,
            instanceName: startTrialInstance,
            isEnrolledInExpandDiscovery,
            migrationSourceUuid,
            targetProduct: startTrialProduct,
          });
        } else {
          this.setState({
            cloudId: startTrialCloudId,
            edition,
            instanceName: startTrialInstance,
            isEnrolledInExpandDiscovery: false,
            migrationSourceUuid,
            targetProduct: startTrialProduct,
          });
        }
      },
    );
  }

  render() {
    if (this.state.isDismissed) {
      return null;
    }
    const {
      analytics,
      featureFlagsStore: { flags },
    } = this.props;
    const {
      cloudId = '',
      targetProduct = '',
      instanceName = '',
      edition,
      migrationSourceUuid,
      isEnrolledInExpandDiscovery,
    } = this.state;
    if (!targetProduct || !instanceName || !cloudId) {
      return null;
    }
    const XFlowComponent = flags['uchi.xflow-use-iframe'] ? XFlowIFrameLazy : XFlowLazy;

    return (
      <XflowErrorBoundary>
        <XFlowComponent
          cloudId={cloudId}
          instanceName={instanceName}
          isCrossSell={false}
          isEnrolledInExpandDiscovery={isEnrolledInExpandDiscovery}
          onAnalyticsEvent={this.onAnalyticsEvent}
          onComplete={this.onComplete}
          sourceComponent="redirect"
          sourceContext="start"
          targetProduct={targetProduct}
          analyticsClient={analytics.getAnalyticsWebClient()}
          edition={edition}
          migrationSourceUuid={migrationSourceUuid}
          onError={this.onXFlowIFrameError}
        />
      </XflowErrorBoundary>
    );
  }

  private onAnalyticsEvent = (event: string, attrs: object) => {
    // xflow-ui doesn't support GASv3 https://growth.jira-dev.com/browse/EXPAND-200
  };

  private onComplete = () => {
    this.setState({ isDismissed: true });
    stripXflowParams();
  };

  private onXFlowIFrameError = async () => {
    const { intl, flagStore } = this.props;
    const flag: InternalFlag = await flagStore.addFlag({
      actions: [
        {
          content: intl.formatMessage(XFlowFlagMessages.retryButtonText),
          onClick: () => this.onXFlowRetry(flag),
        },
      ],
      description: intl.formatMessage(XFlowFlagMessages.errorDescription),
      title: intl.formatMessage(XFlowFlagMessages.errorTitle),
      type: FlagType.ERROR,
    });
    this.setState({ isDismissed: true });
  };

  private onXFlowRetry = async (flag: InternalFlag) => {
    const { id } = await flag;
    this.setState({ isDismissed: false });
    this.props.flagStore.removeFlag(id);
  };

  private tenantHasJSWLicence = (availableSites: Site[], cloudId: string | undefined) => {
    const tenant = availableSites.find(site => site.cloudId === cloudId);
    if (!tenant || !tenant.products) {
      return false;
    }

    const hasJSW = tenant.products.some(product => product.productName === 'Jira Software');
    return hasJSW;
  };
}

export default inject(({ featureFlagsStore, productStore, flagStore }: Store) => ({
  featureFlagsStore,
  flagStore,
  productStore,
}))(injectIntl(withAnalytics(XflowInit)));
